import BugReportRoundedIcon from "@mui/icons-material/BugReportRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import CopyAllRoundedIcon from "@mui/icons-material/CopyAllRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import StickyNote2RoundedIcon from "@mui/icons-material/StickyNote2Rounded";
import TravelExploreRoundedIcon from "@mui/icons-material/TravelExploreRounded";
import type { SxProps, Theme } from "@mui/material";
import { IconButton, Link, Stack, Tooltip, Typography } from "@mui/material";
import type { GridColDef, GridRowClassNameParams } from "@mui/x-data-grid-premium";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import type { ReactElement } from "react";
import { useCallback, useMemo } from "react";
import { getErrorMessage } from "@entrilia/shared/api/axiosHelper";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import StatusChip from "@entrilia/shared/components/StatusChip";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { convertISODateTimeShort } from "@entrilia/shared/utilities/dateUtils";
import { autoFormatCamelCase } from "@entrilia/shared/utilities/stringHelper";
import { nameof } from "@entrilia/shared/utilities/typeHelper";
import type { ObserverIssueInfo, ObserverIssueStatus } from "../../../api/observerTypes";
import { getAppInsightsTransactionUrl } from "./observerAppInsightsLinks";
import { getInvestigationTooltip, resolveInvestigationState } from "./observerInvestigations";

export interface ObserverIssueRow extends ObserverIssueInfo {
  rowType: "issue";
  runDay: string;
}

interface ObserverDayRow {
  id: string;
  rowType: "day";
  runDay: string;
  issueCount: number;
}

export type ObserverGridRow = ObserverIssueRow | ObserverDayRow;

interface Props {
  rows: ObserverGridRow[];
  loading: boolean;
  updatingIssueId?: string;
  canAssign: boolean;
  canEdit: boolean;
  currentUserId: string;
  onOpenAssign: (anchorEl: HTMLElement, issue: ObserverIssueRow) => void;
  onOpenGithubNumber: (anchorEl: HTMLElement, issue: ObserverIssueRow) => void;
  onFileBug: (issue: ObserverIssueRow) => void;
  onSetStatus: (issueId: string, status: ObserverIssueStatus) => void;
  onOpenAcknowledgement: (issue: ObserverIssueRow) => void;
  onReopenIssue: (issueId: string) => void;
  onInvestigate: (issue: ObserverIssueRow) => void;
}

interface ObserverIssueColumnsParams extends Pick<
  Props,
  | "updatingIssueId"
  | "canAssign"
  | "canEdit"
  | "currentUserId"
  | "onOpenAssign"
  | "onOpenGithubNumber"
  | "onFileBug"
  | "onSetStatus"
  | "onOpenAcknowledgement"
  | "onReopenIssue"
  | "onInvestigate"
> {
  onCopyOperationId: (operationId: string) => void;
}

const observerIssueGridColumnCount = 12;

const githubIssuesBaseUrl = "https://github.com/entrilia/entrilia-web-services/issues";

const getGithubIssueUrl = (issueNumber: string) => `${githubIssuesBaseUrl}/${encodeURIComponent(issueNumber)}`;

const statusColor: Record<ObserverIssueStatus, (theme: Theme) => string> = {
  Open: (theme) => theme.palette.text.secondary,
  InProgress: (theme) => theme.palette.warning.main,
  Acknowledged: (theme) => theme.palette.info.main,
  Resolved: (theme) => theme.palette.success.main,
};

const isObserverIssueRow = (row: ObserverGridRow): row is ObserverIssueRow => row.rowType === "issue";
const isObserverDayRow = (row: ObserverGridRow): row is ObserverDayRow => row.rowType === "day";

const withActionTooltip = (title: string, icon: ReactElement) => (
  <Tooltip title={title} arrow>
    {icon}
  </Tooltip>
);

const getRowClassName = ({ row }: GridRowClassNameParams<ObserverGridRow>) =>
  isObserverDayRow(row) ? "observer-day-row" : "";

const gridContainerSx: SxProps<Theme> = { minWidth: 0 };

const gridScrollContainerSx: SxProps<Theme> = { overflowX: "auto" };

const gridSx: SxProps<Theme> = {
  "& .observer-day-row": {
    backgroundColor: "background.grey",
  },
  "& .observer-day-row .MuiDataGrid-cell": {
    borderTopColor: "divider",
  },
};

interface GithubIssueNumberCellProps {
  issue: ObserverIssueRow;
  canEdit: boolean;
  onEdit: (anchorEl: HTMLElement, issue: ObserverIssueRow) => void;
  onFileBug: (issue: ObserverIssueRow) => void;
}

const GithubIssueNumberCell = ({ issue, canEdit, onEdit, onFileBug }: GithubIssueNumberCellProps) => (
  <Stack direction="row" sx={{ alignItems: "center", gap: 0.5, width: "100%" }}>
    {canEdit ? (
      <Tooltip title={issue.githubIssueNumber ? "Edit GitHub issue number" : "Set GitHub issue number"} arrow>
        <IconButton onClick={(event) => onEdit(event.currentTarget, issue)}>
          <EditRoundedIcon />
        </IconButton>
      </Tooltip>
    ) : null}
    {issue.githubIssueNumber ? (
      <Link href={getGithubIssueUrl(issue.githubIssueNumber)} target="_blank" rel="noopener noreferrer">
        {issue.githubIssueNumber}
      </Link>
    ) : null}
    {canEdit && !issue.githubIssueNumber ? (
      <Tooltip title="File a GitHub bug and investigate it — the investigator writes the report into the issue" arrow>
        <IconButton onClick={() => onFileBug(issue)}>
          <BugReportRoundedIcon />
        </IconButton>
      </Tooltip>
    ) : null}
  </Stack>
);

const getObserverIssueColumns = ({
  updatingIssueId,
  canAssign,
  canEdit,
  currentUserId,
  onOpenAssign,
  onOpenGithubNumber,
  onFileBug,
  onSetStatus,
  onOpenAcknowledgement,
  onReopenIssue,
  onInvestigate,
  onCopyOperationId,
}: ObserverIssueColumnsParams): GridColDef<ObserverGridRow>[] => [
  {
    field: nameof<ObserverIssueRow>("issueNumber"),
    headerName: "#",
    width: 72,
    colSpan: (_, row) => (isObserverDayRow(row) ? observerIssueGridColumnCount : undefined),
    renderCell: ({ row }) =>
      isObserverDayRow(row) ? (
        <Typography variant="subtitle2">
          {row.runDay} ({row.issueCount})
        </Typography>
      ) : (
        row.issueNumber
      ),
  },
  {
    field: nameof<ObserverIssueRow>("status"),
    headerName: "Status",
    width: 168,
    renderCell: ({ row }) =>
      isObserverIssueRow(row) ? (
        <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
          <StatusChip label={autoFormatCamelCase(row.status)} color={statusColor[row.status]} />
          {row.status === "Acknowledged" && (
            <Tooltip title="Acknowledgement reason" arrow>
              <span>
                <IconButton disabled={updatingIssueId === row.id} onClick={() => onOpenAcknowledgement(row)}>
                  <StickyNote2RoundedIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      ) : null,
  },
  {
    field: nameof<ObserverIssueRow>("priority"),
    headerName: "Priority",
    width: 128,
  },
  {
    field: nameof<ObserverIssueRow>("assignedToUserName"),
    headerName: "Assignee",
    flex: 1,
    minWidth: 180,
  },
  {
    field: nameof<ObserverIssueRow>("githubIssueNumber"),
    headerName: "GitHub",
    width: 140,
    renderCell: ({ row }) =>
      isObserverIssueRow(row) ? (
        <GithubIssueNumberCell issue={row} canEdit={canEdit} onEdit={onOpenGithubNumber} onFileBug={onFileBug} />
      ) : null,
  },
  {
    field: nameof<ObserverIssueRow>("runAtUtc"),
    headerName: "Run at",
    flex: 1,
    minWidth: 150,
    renderCell: ({ row }) => (isObserverIssueRow(row) ? convertISODateTimeShort(row.runAtUtc) : null),
  },
  {
    field: nameof<ObserverIssueRow>("type"),
    headerName: "Type",
    flex: 1,
    minWidth: 112,
  },
  {
    field: nameof<ObserverIssueRow>("service"),
    headerName: "Service",
    flex: 1,
    minWidth: 160,
  },
  {
    field: nameof<ObserverIssueRow>("shortTitle"),
    headerName: "Issue",
    flex: 3,
    minWidth: 240,
  },
  {
    field: nameof<ObserverIssueRow>("frequency"),
    headerName: "Freq",
    flex: 1,
    minWidth: 112,
  },
  {
    field: nameof<ObserverIssueRow>("whyItMatters"),
    headerName: "Why it matters",
    flex: 3,
    minWidth: 260,
  },
  {
    field: "actions",
    type: "actions",
    headerName: "Actions",
    width: 300,
    getActions: ({ row }) => {
      if (isObserverDayRow(row)) {
        return [];
      }

      const transactionUrl = getAppInsightsTransactionUrl(row);
      const investigationState = resolveInvestigationState(row.investigations, currentUserId);
      return [
        <GridActionsCellItem
          key="copyOperationId"
          icon={withActionTooltip("Copy operation ID", <CopyAllRoundedIcon />)}
          label="Copy operation ID"
          disabled={!row.exampleOperationId}
          onClick={() => {
            if (row.exampleOperationId) {
              onCopyOperationId(row.exampleOperationId);
            }
          }}
        />,
        <GridActionsCellItem
          key="investigate"
          icon={withActionTooltip(
            getInvestigationTooltip(investigationState),
            <TravelExploreRoundedIcon color={investigationState.ownSessionId ? "primary" : undefined} />
          )}
          label="Investigate"
          onClick={() => onInvestigate(row)}
        />,
        <GridActionsCellItem
          key="transaction"
          icon={withActionTooltip("Open App Insights transaction", <OpenInNewRoundedIcon />)}
          label="Open App Insights Transaction"
          disabled={!transactionUrl}
          onClick={() => {
            if (transactionUrl) {
              window.open(transactionUrl, "_blank", "noopener,noreferrer");
            }
          }}
        />,
        <GridActionsCellItem
          key="assign"
          icon={withActionTooltip("Assign issue", <PersonAddAltRoundedIcon />)}
          label="Assign"
          disabled={row.status === "Resolved" || updatingIssueId === row.id || !canAssign}
          onClick={(event) => onOpenAssign(event.currentTarget, row)}
        />,
        <GridActionsCellItem
          key="acknowledge"
          icon={withActionTooltip("Acknowledge issue", <CheckCircleOutlineRoundedIcon />)}
          label="Acknowledge"
          disabled={row.status !== "Open" || updatingIssueId === row.id}
          onClick={() => onOpenAcknowledgement(row)}
        />,
        <GridActionsCellItem
          key="resolve"
          icon={withActionTooltip("Resolve issue", <DoneAllRoundedIcon />)}
          label="Resolve"
          disabled={row.status === "Resolved" || updatingIssueId === row.id}
          onClick={() => onSetStatus(row.id, "Resolved")}
        />,
        <GridActionsCellItem
          key="reopen"
          icon={withActionTooltip("Reopen issue", <ReplayRoundedIcon />)}
          label="Reopen"
          disabled={row.status === "Open" || updatingIssueId === row.id}
          onClick={() => onReopenIssue(row.id)}
        />,
      ];
    },
  },
];

const ObserverIssuesGrid = ({
  rows,
  loading,
  updatingIssueId,
  canAssign,
  canEdit,
  currentUserId,
  onOpenAssign,
  onOpenGithubNumber,
  onFileBug,
  onSetStatus,
  onOpenAcknowledgement,
  onReopenIssue,
  onInvestigate,
}: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const handleCopyOperationId = useCallback(
    async (operationId: string) => {
      try {
        await navigator.clipboard.writeText(operationId);
      } catch (error) {
        sendNotificationError(`Failed to copy: ${getErrorMessage(error)}`);
        return;
      }

      sendNotification("Operation ID copied to clipboard.");
    },
    [sendNotification, sendNotificationError]
  );

  const columns = useMemo(
    () =>
      getObserverIssueColumns({
        updatingIssueId,
        canAssign,
        canEdit,
        currentUserId,
        onOpenAssign,
        onOpenGithubNumber,
        onFileBug,
        onSetStatus,
        onOpenAcknowledgement,
        onReopenIssue,
        onInvestigate,
        onCopyOperationId: handleCopyOperationId,
      }),
    [
      updatingIssueId,
      canAssign,
      canEdit,
      currentUserId,
      onOpenAssign,
      onOpenGithubNumber,
      onFileBug,
      onSetStatus,
      onOpenAcknowledgement,
      onReopenIssue,
      onInvestigate,
      handleCopyOperationId,
    ]
  );

  return (
    <DataGrid<ObserverGridRow>
      rows={rows}
      columns={columns}
      loading={loading}
      disableColumnSorting
      noRowsText="No observer issues"
      containerSx={gridContainerSx}
      scrollContainerSx={gridScrollContainerSx}
      getRowClassName={getRowClassName}
      sx={gridSx}
    />
  );
};

export default ObserverIssuesGrid;
